import { useEffect, useRef, useState } from "react"

import { getPdfJs } from "@/lib/pdf-worker"

interface PdfPageCanvasProps {
  file: File
  pageIndex: number
  scale?: number
  className?: string
  children?: React.ReactNode
  onDimensionsReady?: (dimensions: { width: number; height: number }) => void
}

export function PdfPageCanvas({
  file,
  pageIndex,
  scale = 0.5,
  className,
  children,
  onDimensionsReady,
}: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onDimensionsReadyRef = useRef(onDimensionsReady)

  useEffect(() => {
    onDimensionsReadyRef.current = onDimensionsReady
  }, [onDimensionsReady])

  const [dimensions, setDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    let cancelled = false

    const render = async () => {
      const pdfjsLib = await getPdfJs()
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      const page = await pdf.getPage(pageIndex + 1)
      const viewport = page.getViewport({ scale })

      if (cancelled) return

      const canvas = canvasRef.current
      if (!canvas) return

      canvas.width = viewport.width
      canvas.height = viewport.height

      const context = canvas.getContext("2d")!
      await page.render({ canvas, canvasContext: context, viewport }).promise

      if (!cancelled) {
        setDimensions({ width: viewport.width, height: viewport.height })
        onDimensionsReadyRef.current?.({
          width: viewport.width,
          height: viewport.height,
        })
      }
    }

    void render()

    return () => {
      cancelled = true
    }
  }, [file, pageIndex, scale])

  return (
    <div
      className={className}
      style={{ position: "relative", display: "inline-block" }}
    >
      <canvas ref={canvasRef} className="rounded border" />
      {dimensions && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: dimensions.width,
            height: dimensions.height,
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}
